/**
 * FormFieldRenderer Component
 *
 * 필드 타입에 따라 적절한 필드 컴포넌트를 렌더링하는 팩토리 컴포넌트
 * 코드 스플릿의 핵심: 이 컴포넌트가 모든 필드 타입을 매핑함
 */

import type { FormField } from '@/shared/types/form';
import { Label } from '@/shared/ui/label';

import { CheckboxField } from './CheckboxField';
import { FileField } from './FileField';
import { NumberField } from './NumberField';
import { RadioField } from './RadioField';
import { SelectField } from './SelectField';
import { TextareaField } from './TextareaField';
import { TextField } from './TextField';
import type { BaseFieldProps } from './types';
import { UrlField } from './UrlField';

interface FormFieldRendererProps {
  /** 필드 설정 */
  field: FormField;

  /** 현재 값 */
  value: string | number | boolean;

  /** 값 변경 핸들러 */
  onChange: (value: string | number | boolean) => void;

  /** 비활성화 여부 */
  disabled?: boolean;

  /** 에러 메시지 */
  error?: string;
}

/**
 * 필드 타입에 따른 컴포넌트 맵핑
 */
const FIELD_COMPONENTS: Record<string, React.ComponentType<BaseFieldProps>> = {
  text: TextField,
  number: NumberField,
  url: UrlField,
  textarea: TextareaField,
  select: SelectField,
  dropdown: SelectField, // dropdown은 select의 alias
  radio: RadioField,
  checkbox: CheckboxField,
  file: FileField,
};

/**
 * 동적 폼 필드 렌더러
 *
 * 필드 타입에 따라 적절한 컴포넌트를 선택하여 렌더링
 * 지원하지 않는 타입은 null을 반환
 */
export function FormFieldRenderer({
  field,
  value,
  onChange,
  disabled,
  error,
}: FormFieldRendererProps) {
  const FieldComponent = FIELD_COMPONENTS[field.type];

  // 지원하지 않는 필드 타입인 경우
  if (!FieldComponent) {
    console.warn(`[FormFieldRenderer] Unsupported field type: ${field.type}`);
    return null;
  }

  // 라벨을 숨겨야 하는 경우:
  // 1. checkbox: 컴포넌트 내부에서 라벨 처리
  // 2. file: Figma 디자인에 따라 라벨 없이 표시
  // 3. visibleWhen이 있는 필드: 부모 드롭다운의 맥락에서 의미가 명확하므로 라벨 불필요
  const showLabel = field.type !== 'checkbox' && field.type !== 'file' && !field.visibleWhen;

  // visibleWhen이 있는 필드는 부모 드롭다운과 더 가까이 붙어야 함 (Figma 디자인)
  // 일반 필드: gap-3 (12px), 조건부 필드: gap 없음 (상위 InputPanel에서 처리)
  const gapClass = field.visibleWhen ? '' : 'gap-3';

  return (
    <div className={`flex flex-col ${gapClass}`}>
      {showLabel && (
        <Label htmlFor={field.id} className="text-base font-normal">
          {field.label}
          {field.required && <span className="text-red-500">*</span>}
        </Label>
      )}

      <FieldComponent
        field={field}
        value={value}
        onChange={onChange}
        disabled={disabled}
        error={error}
      />
    </div>
  );
}
